import { useState, useRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDndMonitor,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMyApps, useReorderMyApps, useResetMyAppsOrder } from "../hooks/useApps";
import { useAuthStore } from "../store/auth.store";
import type { App } from "../api/apps.api";

export function Dashboard() {
  const { data: serverApps, isLoading, error } = useMyApps();
  const isAdmin = useAuthStore((s) => s.user?.isPanelAdmin === true);
  const reorder = useReorderMyApps();
  const resetOrder = useResetMyAppsOrder();

  // Optimistic local order so drags feel instant
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  // Cards only become draggable once the user explicitly unlocks reordering;
  // otherwise dnd-kit's touch listeners would fight native scroll on mobile.
  const [isEditMode, setIsEditMode] = useState(false);

  const apps = serverApps
    ? localOrder
      ? [...serverApps].sort(
          (a, b) => (localOrder.indexOf(a.id) ?? 0) - (localOrder.indexOf(b.id) ?? 0),
        )
      : serverApps
    : [];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 10 } }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id || !serverApps) return;
    const ids = apps.map((a) => a.id);
    const newIds = arrayMove(ids, ids.indexOf(active.id as string), ids.indexOf(over.id as string));
    setLocalOrder(newIds);
    reorder.mutate(newIds, { onError: () => setLocalOrder(null) });
  }

  function handleResetOrder() {
    setLocalOrder(null);
    resetOrder.mutate();
  }

  if (!isAdmin) {
    return (
      <div className="dashboard-plain">
        <div className="page-header" style={{ justifyContent: "flex-start" }}>
          <h2>Your apps</h2>
          {apps.length > 0 && (
            <div style={{ display: "flex", gap: 8, marginLeft: 25 }}>
              <button
                onClick={() => setIsEditMode((v) => !v)}
                style={{ background: "transparent", border: "none", color: "var(--text-muted)", fontSize: 12, cursor: "pointer", padding: "2px 4px" }}
              >
                {isEditMode ? "Done" : "Reorder"}
              </button>
              {isEditMode && (
                <button
                  onClick={handleResetOrder}
                  disabled={resetOrder.isPending}
                  style={{ background: "transparent", border: "none", color: "var(--text-muted)", fontSize: 12, cursor: "pointer", padding: "2px 4px" }}
                >
                  Reset order
                </button>
              )}
            </div>
          )}
        </div>

        {isLoading && <p className="muted">Loading…</p>}
        {error && <p className="error-text">Failed to load apps</p>}

        {apps.length === 0 && !isLoading && !error && (
          <p className="muted">You don't have access to any apps yet.</p>
        )}

        {apps.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={apps.map((a) => a.id)} strategy={rectSortingStrategy}>
              <div className={`plain-grid${isEditMode ? " is-editing" : ""}`}>
                {apps.map((app) => (
                  <SortablePlainTile key={app.id} app={app} isEditMode={isEditMode} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="page-header" style={{ justifyContent: "flex-start" }}>
        <h2>Your apps</h2>
        <div style={{ display: "flex", gap: 8, marginLeft: 25 }}>
          <button
            onClick={() => setIsEditMode((v) => !v)}
            style={{ background: "transparent", border: "none", color: "var(--text-muted)", fontSize: 12, cursor: "pointer", padding: "2px 4px" }}
          >
            {isEditMode ? "Done" : "Reorder"}
          </button>
          {isEditMode && (
            <button
              onClick={handleResetOrder}
              disabled={resetOrder.isPending}
              style={{ background: "transparent", border: "none", color: "var(--text-muted)", fontSize: 12, cursor: "pointer", padding: "2px 4px" }}
            >
              Reset order
            </button>
          )}
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="error-text">Failed to load apps</p>}

      {apps.length === 0 && !isLoading && !error && (
        <div className="card">
          <p className="muted">You don't have access to any apps yet.</p>
          <p className="muted" style={{ fontSize: 13 }}>
            Ask a panel admin to grant you access from the Users page.
          </p>
        </div>
      )}

      {apps.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={apps.map((a) => a.id)} strategy={rectSortingStrategy}>
            <div className={`tile-grid${isEditMode ? " is-editing" : ""}`}>
              {apps.map((app) => (
                <SortableAdminTile key={app.id} app={app} isEditMode={isEditMode} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </>
  );
}

// Suppresses the post-drag click using dnd-kit's internal drag lifecycle.
// useDndMonitor fires onDragStart/End/Cancel for every drag in the ancestor
// DndContext, regardless of direction. We keep a ref true during the drag and
// for a short window after it ends/cancels, then block any click in that window.
function useDragClickGuard(isEditMode: boolean, extra?: (e: React.MouseEvent) => void) {
  const dragging = useRef(false);
  // touchMoved tracks ANY finger movement during a touch sequence. A swipe that
  // doesn't reach dnd-kit's activation distance still moves the finger, and we
  // must not open the URL in that case.
  const touchMoved = useRef(false);
  useDndMonitor({
    onDragStart() { dragging.current = true; },
    onDragEnd() { setTimeout(() => { dragging.current = false; }, 500); },
    onDragCancel() { setTimeout(() => { dragging.current = false; }, 500); },
  });
  return {
    onTouchMove: () => { touchMoved.current = true; },
    onTouchEnd: (e: React.TouchEvent) => {
      const moved = touchMoved.current;
      touchMoved.current = false;
      if (dragging.current || moved) e.preventDefault();
    },
    onClick: (e: React.MouseEvent) => {
      // While unlocked for reordering, taps are for rearranging, not navigating.
      if (isEditMode || dragging.current) { e.preventDefault(); return; }
      extra?.(e);
    },
  };
}

function SortablePlainTile({ app, isEditMode }: { app: App; isEditMode: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: app.id,
    disabled: !isEditMode,
  });
  const isNonProd = app.environment !== "PRODUCTION";
  const guard = useDragClickGuard(isEditMode, isNonProd ? (e) => e.preventDefault() : undefined);
  return (
    <a
      ref={setNodeRef}
      style={{
        transform: [CSS.Transform.toString(transform), isDragging ? "scale(1.06)" : undefined]
          .filter(Boolean)
          .join(" "),
        transition,
        opacity: isDragging ? 0.9 : isNonProd ? 0.35 : 1,
        filter: isNonProd ? "grayscale(1)" : undefined,
        cursor: isEditMode ? (isDragging ? "grabbing" : "grab") : undefined,
        touchAction: isEditMode ? "none" : undefined,
        boxShadow: isDragging ? "0 0 0 1px rgba(255,255,255,0.2), 0 16px 32px rgba(0,0,0,0.6)" : undefined,
        zIndex: isDragging ? 1 : undefined,
        position: isDragging ? "relative" : undefined,
        animationPlayState: isDragging ? "paused" : undefined,
      }}
      {...attributes}
      {...(isEditMode ? listeners : undefined)}
      {...guard}
      href={isNonProd ? undefined : app.url}
      target={isNonProd ? undefined : "_blank"}
      rel="noopener noreferrer"
      className="plain-tile"
      title={isNonProd ? "Under Development" : (app.description ?? undefined)}
      aria-disabled={isNonProd || undefined}
    >
      <div className="plain-tile-icon">
        <TileIcon logoUrl={app.logoUrl} iconUrl={app.iconUrl} name={app.name} />
      </div>
      <div className="plain-tile-name">{app.name}</div>
    </a>
  );
}

function SortableAdminTile({ app, isEditMode }: { app: App; isEditMode: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: app.id,
    disabled: !isEditMode,
  });
  const guard = useDragClickGuard(isEditMode);
  return (
    <a
      ref={setNodeRef}
      style={{
        transform: [CSS.Transform.toString(transform), isDragging ? "scale(1.06)" : undefined]
          .filter(Boolean)
          .join(" "),
        transition,
        opacity: isDragging ? 0.9 : 1,
        cursor: isEditMode ? (isDragging ? "grabbing" : "grab") : undefined,
        touchAction: isEditMode ? "none" : undefined,
        boxShadow: isDragging ? "0 16px 32px rgba(0,0,0,0.35)" : undefined,
        zIndex: isDragging ? 1 : undefined,
        position: isDragging ? "relative" : undefined,
        animationPlayState: isDragging ? "paused" : undefined,
      }}
      {...attributes}
      {...(isEditMode ? listeners : undefined)}
      {...guard}
      href={app.url}
      target="_blank"
      rel="noopener noreferrer"
      className="tile"
    >
      <div className="tile-icon">
        <TileIcon logoUrl={app.logoUrl} iconUrl={app.iconUrl} name={app.name} />
      </div>
      <div className="tile-body">
        <div className="tile-name">{app.name}</div>
        {app.description && <div className="tile-desc">{app.description}</div>}
      </div>
    </a>
  );
}

function TileIcon({ logoUrl, iconUrl, name }: { logoUrl?: string | null; iconUrl?: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  const src = logoUrl || iconUrl;
  if (src && !failed) {
    return <img src={src} alt="" onError={() => setFailed(true)} />;
  }
  return <span>{initials(name)}</span>;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
